/**
 * designTokens.js — FONTE ÚNICA dos tokens de design do painel.
 *
 * O painel tem TRÊS camadas de estilo, e cada uma tem um dono (docs/ANALISE-TEMAS-MARCA-VS-OPERACAO.md):
 *   1) ESCALA OPERACIONAL por modo (claro/escuro) — `operationalModes.js`. Global: fundo, superfície,
 *      texto, estados, tints, cinzas, sombras. Igual nos 13 temas.
 *   2) ACENTO do nicho por modo — vem do tema (`admin.accent` do {id}.json). O ACENTO PADRÃO (fallback
 *      estático) é LIDO do `themes/templates/default.json` embutido — não há cópia aqui.
 *   3) FIXOS — tipografia de corpo, escala de tamanhos, raios, espaçamento, verdes do WhatsApp.
 *
 * A partir deste módulo:
 *   - o tema MUI base é montado (App.jsx consome `muiPalette` / `shape`; a escala tipográfica é do muiThemeConfig);
 *   - o `src/styles/tokens.css` é GERADO por `scripts/generate-tokens.mjs` (prebuild/predev) e a
 *     MESMA saída é copiada para o pacote do Claude Design. `:root` = fixos + modo claro + acento
 *     padrão claro; `:root[data-color-mode="dark"]` = modo escuro + acento padrão escuro.
 *
 * ⚠️ NUNCA edite `tokens.css` na mão — ele é gerado. Edite os VALORES aqui / em operationalModes.js.
 */
import { brandColors } from '../constants/brandColors.js';
import { operationalModes } from './operationalModes.js';
// Import attribute: o mesmo arquivo é lido pelo Vite (app/testes) e pelo Node (generate-tokens.mjs).
import defaultTheme from '../themes/templates/default.json' with { type: 'json' };

export const COLOR_MODES = ['light', 'dark'];

// ── Acento: chave do JSON de tema (`admin.accent.<modo>`) → CSS var ──────────────────────────
//
// CTA comercial (`cta`/`ctaDark`) — NÃO é a primária. Só para o botão em que o interesse do clique
// é NOSSO (domínio, pagamento, créditos, publicar a 1ª página). A primária é a cor do NICHO — em
// Tatuagem é vermelha, e vermelho em botão de compra lê como "pare". ⚠️ Requisito: ≥ 4,5:1 contra
// o `onPrimary` do MESMO tema e modo (texto 16px bold não é "texto grande" na WCAG). Não usar
// `--color-success` aqui (3,45:1 com branco).
export const ACCENT_TO_VAR = {
  primary: '--color-primary',
  primaryDark: '--color-primary-dark',     // tom de TEXTO/ÍCONE da marca sobre a superfície do modo
  primaryLight: '--color-primary-light',
  primarySoft: '--color-primary-soft',     // fundo suave da primária (chip/hover/selected)
  onPrimary: '--on-primary',               // texto/ícone sobre a primária
  secondary: '--color-secondary',
  cta: '--color-cta',
  ctaDark: '--color-cta-dark',
  drawerBackground: '--bg-drawer',         // faixa do menu — onde a marca pode ser "alta"
  drawerText: '--text-drawer',
  drawerAccent: '--drawer-accent',         // tom de marca legível sobre o drawer
};
export const ACCENT_KEYS = Object.keys(ACCENT_TO_VAR);

// Acento PADRÃO por modo = `accent` do default.json embutido (o import-design o mantém igual ao
// Static). Tema sem acento para um modo cai aqui. Chave faltando no JSON quebra na carga, não em tela.
export const accentDefaults = Object.fromEntries(COLOR_MODES.map((mode) => {
  const accent = defaultTheme.accent?.[mode] || {};
  const missing = ACCENT_KEYS.filter((key) => accent[key] == null);
  if (missing.length) throw new Error(`default.json sem accent.${mode}: ${missing.join(', ')}`);
  return [mode, Object.fromEntries(ACCENT_KEYS.map((key) => [key, accent[key]]))];
}));

// ── Forma + espaçamento (FIXOS — as sombras são do modo, em operationalModes) ─────────────────
export const shape = {
  radiusCard: 14,
  radiusCardMobile: 8,
  radiusSm: 9,
  radiusLg: 20,
};
export const spacingBase = 8;

// ── Tipografia ────────────────────────────────────────────────────────────────────────────────
// Corpo FIXO (D19): densidade e legibilidade iguais em todas as contas.
// Título: fonte do nicho (`admin.fontHeading`), SÓ em h1/h2 (D18). O padrão vem do default.json.
export const fontFamily = "'DM Sans', 'Segoe UI', system-ui, -apple-system, sans-serif";
export const fontHeading = defaultTheme.fontHeading;

// A ESCALA TIPOGRÁFICA (tamanhos, pesos, redução no celular) mora em `muiThemeConfig.js`: é o ambiente
// pinado que o build:design copia para o pacote — assim `<Typography variant="h1">` tem o mesmo tamanho
// no pacote do Claude Design e no Admin. Aqui só ficam os tamanhos desktop expostos como --fs-*.

// Tamanhos desktop expostos como CSS var (--fs-*). Nomes = variantes da escala.
const fontSizeVars = {
  h1: '1.75rem', h2: '1.5rem', h3: '1.25rem', h4: '1.5rem', h5: '1.25rem', h6: '1.0625rem',
  subtitle1: '1rem', subtitle2: '0.875rem', body1: '1rem', body2: '0.875rem',
  caption: '0.75rem', overline: '0.6875rem',
};

// ── Palette MUI base (modo claro + acento padrão) — o ThemeApplier troca por tenant/modo ──────
const light = operationalModes.light;
export const muiPalette = {
  mode: 'light',
  primary: { main: accentDefaults.light.primary },
  secondary: { main: accentDefaults.light.secondary },
  background: { default: light['--bg-page'], paper: light['--surface'] },
  text: { primary: light['--text-primary'], secondary: light['--text-secondary'], disabled: light['--text-disabled'] },
  divider: light['--divider'],
};

/**
 * Gera o CSS completo do tokens.css (blocos `:root` e `:root[data-color-mode="dark"]`).
 * Usado por scripts/generate-tokens.mjs. Não formata nada à mão em outro lugar.
 */
export function toCssRules() {
  const block = (selector, build) => {
    const L = [];
    const g = (name, val, comment) => L.push(`  ${name}: ${val};${comment ? `   /* ${comment} */` : ''}`);
    const sec = (t) => L.push(`\n  /* ── ${t} ── */`);
    build(g, sec);
    return `${selector} {\n${L.join('\n')}\n}\n`;
  };
  const emitMode = (mode, g, sec) => {
    sec(`Escala operacional — modo ${mode} (global, não segue tema)`);
    for (const [name, val] of Object.entries(operationalModes[mode])) g(name, val);
    sec(`Acento PADRÃO — modo ${mode} (o tema do nicho sobrescreve em runtime)`);
    for (const [key, name] of Object.entries(ACCENT_TO_VAR)) g(name, accentDefaults[mode][key]);
  };

  const root = block(':root', (g, sec) => {
    g('color-scheme', 'light');
    emitMode('light', g, sec);

    sec('WhatsApp brand greens (brandColors.whatsapp) — fixos');
    g('--wa-main', brandColors.whatsapp.main);
    g('--wa-action', brandColors.whatsapp.action);
    g('--wa-bubble', brandColors.whatsapp.bubble);
    g('--wa-chat-bg', brandColors.whatsapp.chatBg);
    g('--wa-header', brandColors.whatsapp.header);
    g('--wa-on-main', brandColors.whatsapp.onMain, 'texto/ícone sobre --wa-main (7,31:1)');

    sec('Tipografia — corpo fixo; --font-heading = só h1/h2, o tema sobrescreve');
    g('--font-family', fontFamily);
    g('--font-heading', fontHeading);
    g('--fw-regular', 400); g('--fw-medium', 500); g('--fw-bold', 700);
    for (const [k, v] of Object.entries(fontSizeVars)) g(`--fs-${k}`, v);

    sec('Forma + espaçamento — fixos');
    g('--radius-card', `${shape.radiusCard}px`);
    g('--radius-card-mobile', `${shape.radiusCardMobile}px`);
    g('--radius-sm', `${shape.radiusSm}px`);
    g('--radius-lg', `${shape.radiusLg}px`);
    for (let i = 1; i <= 4; i++) g(`--space-${i}`, `${spacingBase * i}px`);
  });

  const dark = block(':root[data-color-mode="dark"]', (g, sec) => {
    g('color-scheme', 'dark');
    emitMode('dark', g, sec);
  });

  return `${root}\n${dark}`;
}

export { brandColors, operationalModes };
